/**
 * Shrinks images in the browser before they are uploaded.
 *
 * A phone photo is routinely 4–8 MB; re-encoding to WebP at a sane edge length
 * typically lands under 300 KB with no visible difference in a note. Everything
 * here degrades to "upload the original" rather than failing an upload.
 */

/** Long edge, in device pixels. Comfortably sharp on a retina screen. */
export const MAX_EDGE = 2048
export const QUALITY = 0.82
/** Below this, re-encoding usually costs more bytes than it saves. */
export const SKIP_UNDER_BYTES = 180 * 1024

/** Formats that must be uploaded untouched. */
const NEVER_TOUCH = new Set([
  'image/gif',    // re-encoding would drop the animation
  'image/svg+xml' // vector; already tiny and would rasterise
])

export function shouldCompress(file: File): boolean {
  if (!file.type.startsWith('image/')) return false
  if (NEVER_TOUCH.has(file.type)) return false
  return file.size > SKIP_UNDER_BYTES
}

function targetSize(w: number, h: number): { w: number; h: number } {
  const longest = Math.max(w, h)
  if (longest <= MAX_EDGE) return { w, h }
  const scale = MAX_EDGE / longest
  return { w: Math.round(w * scale), h: Math.round(h * scale) }
}

async function decode(file: File): Promise<{ bitmap: ImageBitmap | HTMLImageElement; w: number; h: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    return { bitmap, w: bitmap.width, h: bitmap.height }
  }
  // Safari fallbacks and anything without createImageBitmap.
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const el = new Image()
      el.onload = () => res(el)
      el.onerror = () => rej(new Error('decode failed'))
      el.src = url
    })
    return { bitmap: img, w: img.naturalWidth, h: img.naturalHeight }
  } finally {
    URL.revokeObjectURL(url)
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((res) => canvas.toBlob(res, type, quality))
}

export interface CompressResult {
  file: File
  originalBytes: number
  bytes: number
  /** False when the original was kept — too small, wrong format, or no saving. */
  compressed: boolean
}

export async function compressImage(file: File): Promise<CompressResult> {
  const untouched: CompressResult = {
    file, originalBytes: file.size, bytes: file.size, compressed: false,
  }
  if (!shouldCompress(file)) return untouched

  try {
    const { bitmap, w, h } = await decode(file)
    const size = targetSize(w, h)
    const canvas = document.createElement('canvas')
    canvas.width = size.w
    canvas.height = size.h
    const ctx = canvas.getContext('2d')
    if (!ctx) return untouched
    ctx.drawImage(bitmap as CanvasImageSource, 0, 0, size.w, size.h)
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close()

    // WebP first: it beats JPEG at the same quality and keeps transparency,
    // which matters for screenshots pasted from a design tool.
    let blob = await toBlob(canvas, 'image/webp', QUALITY)
    let type = 'image/webp'
    if (!blob || blob.type !== 'image/webp') {
      type = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
      blob = await toBlob(canvas, type, QUALITY)
    }
    if (!blob) return untouched
    // Re-encoding can enlarge an already-optimised file; keep whichever is smaller.
    if (blob.size >= file.size) return untouched

    const ext = type === 'image/webp' ? 'webp' : type === 'image/png' ? 'png' : 'jpg'
    const base = file.name.replace(/\.[^.]+$/, '') || 'image'
    return {
      file: new File([blob], `${base}.${ext}`, { type, lastModified: Date.now() }),
      originalBytes: file.size,
      bytes: blob.size,
      compressed: true,
    }
  } catch {
    return untouched // a format the browser cannot decode, e.g. some HEIC
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
